'use client'

import { useCurrentUser } from '@/hooks/use-current-user'
import { AppointmentItem } from '@/lib/types/operations/appointment'
import { showToast } from '@/lib/utils/toast'
import { useRouter } from 'next/navigation'
import { useCallback, useEffect, useState } from 'react'

// Define interface for enhanced appointment details
interface AppointmentDetails {
  id: string
  createdAt: string
  createdAtFormatted: string
  hotlinkSlug: string
  servicesCount: number
  location: string
  vehicle: string
}

// Define interface for enhanced pending appointment data
interface EnhancedAppointmentItem extends AppointmentItem {
  totalAmount?: number
  createdAtFormatted?: string
  daysSincePending?: number
}

/**
 * Hook to fetch pending appointments for the current user that require payment
 * and provide functions to handle them
 */
export function usePendingAppointments() {
  const router = useRouter()
  const user = useCurrentUser()
  const [pendingAppointments, setPendingAppointments] = useState<EnhancedAppointmentItem[]>([])
  const [appointmentDetails, setAppointmentDetails] = useState<AppointmentDetails | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [confirmModalOpen, setConfirmModalOpen] = useState(false)

  const fetchPendingAppointments = useCallback(async () => {
    if (!user?.id) {
      setIsLoading(false)
      setPendingAppointments([])
      setAppointmentDetails(null)
      setError(null)
      return
    }

    try {
      setIsLoading(true)
      setError(null)
      const response = await fetch('/api/appointments/pending')

      if (!response.ok) {
        if (response.status === 401) {
          // User is not authenticated, this is expected for unauthenticated users
          setPendingAppointments([])
          setAppointmentDetails(null)
          return
        }
        throw new Error('Failed to fetch pending appointments')
      }

      const data = await response.json()
      setPendingAppointments(data.appointments || [])
      setAppointmentDetails(data.appointmentDetails || null)
    } catch (err) {
      console.error('Error fetching pending appointments:', err)
      setError(err instanceof Error ? err.message : 'Unknown error')
      setPendingAppointments([])
      setAppointmentDetails(null)
    } finally {
      setIsLoading(false)
    }
  }, [user?.id])

  useEffect(() => {
    fetchPendingAppointments()
  }, [fetchPendingAppointments])

  // Get the oldest pending appointment (if any)
  const oldestPendingAppointment =
    pendingAppointments.length > 0
      ? pendingAppointments.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())[0]
      : null

  // Function to navigate to payment page for oldest pending appointment
  const goToPayment = useCallback(() => {
    if (oldestPendingAppointment?.hotlinkSlug) {
      router.push(`/pay/${oldestPendingAppointment.hotlinkSlug}`)
    } else {
      showToast({ title: 'No pending appointment found to pay for', color: 'danger', timeout: 5000 })
    }
  }, [oldestPendingAppointment, router])

  // Function to reset pending appointments (cancel oldest)
  const resetPendingAppointment = useCallback(async () => {
    if (!oldestPendingAppointment?.id) {
      showToast({ title: 'No pending appointment to reset', color: 'danger', timeout: 5000 })
      return
    }

    try {
      setIsLoading(true)
      const response = await fetch(`/api/appointments/${oldestPendingAppointment.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          reason: 'User requested cancellation to create new appointment'
        })
      })

      if (!response.ok) {
        throw new Error('Failed to reset pending appointment')
      }

      showToast({ title: 'Your pending appointment has been reset', color: 'success', timeout: 3000 })
      // Refresh the list of pending appointments
      await fetchPendingAppointments()
    } catch (err) {
      console.error('Error resetting pending appointment:', err)
      showToast({ title: 'Failed to reset your pending appointment', color: 'danger', timeout: 5000 })
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setIsLoading(false)
    }
  }, [oldestPendingAppointment, fetchPendingAppointments])

  // Function to handle opening confirm modal
  const openConfirmCancelModal = useCallback(() => {
    setConfirmModalOpen(true)
  }, [])

  // Function to handle closing confirm modal
  const closeConfirmCancelModal = useCallback(() => {
    setConfirmModalOpen(false)
  }, [])

  return {
    pendingAppointments,
    oldestPendingAppointment,
    appointmentDetails,
    isLoading,
    error,
    goToPayment,
    resetPendingAppointment,
    openConfirmCancelModal,
    closeConfirmCancelModal,
    confirmModalOpen,
    refresh: fetchPendingAppointments,
    hasPendingAppointments: pendingAppointments.length > 0
  }
}
